import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ComplianceRecord, DashboardStats } from '../../../types';
import { reportingApi } from '../../reporting/api';
import {
  BarChart3,
  TrendingUp,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Activity,
  PieChart,
  Filter
} from 'lucide-react';

const Reports: React.FC = () => {
  const { user, hasPermission } = useAuth();

  const canViewReports = hasPermission('reports.view');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [records, setRecords] = useState<ComplianceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [chartType, setChartType] = useState<'compliance' | 'emissions' | 'trends'>('compliance');

  useEffect(() => {
    loadReportData();
  }, [timeRange]);

  const loadReportData = async () => {
    try {
      const [statsData, recordsData] = await Promise.all([
        reportingApi.getDashboardStats(),
        reportingApi.getComplianceRecords()
      ]);
      setStats(statsData);
      setRecords(recordsData);
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getComplianceData = () => {
    if (!stats) return [];

    const total = stats.totalRecords;
    const approved = stats.approvedRecords;
    const submitted = stats.submittedRecords;
    const pending = stats.pendingReview;

    return [
      { name: 'Approved', value: approved, color: '#10b981' },
      { name: 'Submitted', value: submitted, color: '#3b82f6' },
      { name: 'Pending Review', value: pending, color: '#f59e0b' },
      { name: 'Not Started', value: total - approved - submitted - pending, color: '#6b7280' }
    ];
  };

  const getEmissionsData = () => {
    return [
      { month: 'Jan', scope1: 120, scope2: 80, scope3: 45 },
      { month: 'Feb', scope1: 115, scope2: 75, scope3: 42 },
      { month: 'Mar', scope1: 110, scope2: 70, scope3: 38 },
      { month: 'Apr', scope1: 105, scope2: 65, scope3: 35 },
      { month: 'May', scope1: 100, scope2: 60, scope3: 32 },
      { month: 'Jun', scope1: 95, scope2: 55, scope3: 30 }
    ];
  };

  const getTrendsData = () => {
    return [
      { month: 'Jan', compliance: 85, submissions: 12 },
      { month: 'Feb', compliance: 88, submissions: 15 },
      { month: 'Mar', compliance: 82, submissions: 18 },
      { month: 'Apr', compliance: 90, submissions: 14 },
      { month: 'May', compliance: 87, submissions: 16 },
      { month: 'Jun', compliance: 92, submissions: 20 }
    ];
  };

  const exportReport = (format: 'pdf' | 'excel' | 'csv') => {
    alert(`Exporting report as ${format.toUpperCase()}...`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-slate-600 animate-spin" size={48} />
          <p className="text-slate-600 font-medium">Loading reports...</p>
        </div>
      </div>
    );
  }

  if (!canViewReports) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-red-600 px-8 py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Access Denied</h2>
                <p className="text-red-100 text-sm mt-0.5">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-8">
            <p className="text-slate-600">Only authorized users can access Reports & Analytics.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="bg-blue-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <BarChart3 className="text-white" size={32} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Reports & Analytics</h1>
                  <p className="text-indigo-100 mt-1">Welcome back, {user?.first_name} {user?.last_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <Filter className="text-indigo-200" size={20} />
                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value as any)}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-indigo-600 backdrop-blur-sm"
                  >
                    <option value="7d" className="text-slate-900">Last 7 Days</option>
                    <option value="30d" className="text-slate-900">Last 30 Days</option>
                    <option value="90d" className="text-slate-900">Last 90 Days</option>
                    <option value="1y" className="text-slate-900">Last Year</option>
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => exportReport('pdf')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 transition-all backdrop-blur-sm"
                  >
                    <Download size={16} />
                    PDF
                  </button>
                  <button
                    onClick={() => exportReport('excel')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 transition-all backdrop-blur-sm"
                  >
                    <Download size={16} />
                    Excel
                  </button>
                  <button
                    onClick={() => exportReport('csv')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 transition-all backdrop-blur-sm"
                  >
                    <Download size={16} />
                    CSV
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <FileText className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-600 font-medium">Total Records</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.totalRecords}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <CheckCircle className="text-emerald-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-600 font-medium">Compliance Rate</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {stats.totalRecords > 0
                      ? `${Math.round((stats.approvedRecords / stats.totalRecords) * 100)}%`
                      : '0%'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Clock className="text-amber-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-600 font-medium">Pending Review</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.pendingReview}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-600 font-medium">Overdue Tasks</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.overdueTasks}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Compliance Status Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <PieChart className="text-slate-600" size={24} />
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Compliance Status</h3>
                    <p className="text-sm text-slate-600">Current compliance overview</p>
                  </div>
                </div>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value as any)}
                  className="px-4 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                >
                  <option value="compliance">Compliance</option>
                  <option value="emissions">Emissions</option>
                  <option value="trends">Trends</option>
                </select>
              </div>
            </div>

            <div className="p-6">
              {chartType === 'compliance' && stats && (
                <div className="flex items-center justify-center gap-8">
                  <div className="relative w-48 h-48">
                    {getComplianceData().map((segment, index) => {
                      const percentage = stats.totalRecords > 0 ? (segment.value / stats.totalRecords) * 100 : 0;
                      const angle = (percentage / 100) * 360;
                      const previousAngle = getComplianceData().slice(0, index).reduce((sum, s) => sum + (stats.totalRecords > 0 ? (s.value / stats.totalRecords) * 100 : 0), 0);

                      return (
                        <div key={segment.name} className="absolute inset-0">
                          <div
                            className="absolute inset-0 rounded-full"
                            style={{
                              background: segment.color,
                              clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * Math.cos((previousAngle - 90) * Math.PI / 180)}% ${50 + 50 * Math.sin((previousAngle - 90) * Math.PI / 180)}%, ${50 + 50 * Math.cos((previousAngle + angle - 90) * Math.PI / 180)}% ${50 + 50 * Math.sin((previousAngle + angle - 90) * Math.PI / 180)}%)`
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="space-y-3">
                    {getComplianceData().map((segment) => (
                      <div key={segment.name} className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: segment.color }}></div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{segment.name}</p>
                          <p className="text-xs text-slate-500">{segment.value} records</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {chartType === 'emissions' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Activity className="text-slate-600" size={20} />
                    <h4 className="font-semibold text-slate-900">Emissions by Scope</h4>
                  </div>
                  <div className="grid grid-cols-6 gap-4">
                    {getEmissionsData().map((data) => (
                      <div key={data.month} className="text-center">
                        <p className="text-sm font-medium text-slate-900 mb-2">{data.month}</p>
                        <div className="space-y-1">
                          <div className="flex items-end gap-1">
                            <div className="w-3 bg-blue-500 rounded-t" style={{ height: `${(data.scope1 / 120) * 60}px` }}></div>
                            <div className="w-3 bg-green-500 rounded-t" style={{ height: `${(data.scope2 / 120) * 60}px` }}></div>
                            <div className="w-3 bg-yellow-500 rounded-t" style={{ height: `${(data.scope3 / 120) * 60}px` }}></div>
                          </div>
                          <div className="text-xs text-slate-500 space-y-0.5">
                            <p>S1: {data.scope1}</p>
                            <p>S2: {data.scope2}</p>
                            <p>S3: {data.scope3}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded"></div>
                      <span className="text-xs text-slate-600">Scope 1</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded"></div>
                      <span className="text-xs text-slate-600">Scope 2</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                      <span className="text-xs text-slate-600">Scope 3</span>
                    </div>
                  </div>
                </div>
              )}

              {chartType === 'trends' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="text-slate-600" size={20} />
                    <h4 className="font-semibold text-slate-900">Compliance Trends</h4>
                  </div>
                  <div className="grid grid-cols-6 gap-4">
                    {getTrendsData().map((data) => (
                      <div key={data.month} className="text-center">
                        <p className="text-sm font-medium text-slate-900 mb-2">{data.month}</p>
                        <div className="space-y-2">
                          <div className="h-16 bg-slate-100 rounded-lg relative overflow-hidden">
                            <div
                              className="absolute bottom-0 left-0 right-0 bg-blue-500 rounded-b-lg transition-all"
                              style={{ height: `${(data.compliance / 100) * 64}px` }}
                            ></div>
                            <div className="absolute top-1 left-1/2 transform -translate-x-1/2 text-xs font-bold text-white">
                              {data.compliance}%
                            </div>
                          </div>
                          <div className="text-xs text-slate-500">
                            <p>Submissions: {data.submissions}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity Table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <Activity className="text-slate-600" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Recent Compliance Activity</h3>
                  <p className="text-sm text-slate-600">Latest submissions and reviews</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Template</th>
                      <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Company</th>
                      <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Score</th>
                      <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {records.slice(0, 8).map((record) => (
                      <tr key={record.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <div className="text-sm font-medium text-slate-900">{record.template_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                          {record.company?.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            record.status === 'approved' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                            record.status === 'submitted' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                            record.status === 'under_review' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                            record.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-200' :
                            'bg-slate-100 text-slate-800 border border-slate-200'
                          }`}>
                            {record.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                          {record.compliance_score ? `${record.compliance_score}%` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-500">
                          {record.submitted_at ? new Date(record.submitted_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;